import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatQuantity } from '../utils/formatters';
import { formatDate } from '../utils/formatterDate';
import { useToast } from '../contexts/ToastContext';
import ConfirmationModal from './Shared/ConfirmationModal';
import ItemViewPublic from './Shared/ItemViewPublic';
import PriceInput from './Shared/PriceInput';
import {
  fetchQuotePublicDetail,
  updateQuotePublicItem,
  updateQuotePublic,
  sendQuotePublicConfirmation,
  selectQuotePublicCurrentQuote,
  selectQuotePublicQuoteItems,
  selectIsQuotePublicDetailLoading,
  selectQuotePublicDetailError
} from '../store/slices/quotePublicSlice';
import { AppDispatch } from '../store/index';

import MasLogo from '../images/MAS.png';

interface Product {
  id: number;
  itemId: number; // itemId ekledik
  code: string;
  name: string;
  requestedQuantity: number;
  quantity: number;
  targetRequestedQuantity: number;
  unit: string;
  unitPrice: number;
  targetRequestedPrice: number;
  deliveryDays: number;
  targetDeliveryDays: number;
  vatRate: number;
  total: number;
  isApproved: boolean;
  isEdited: boolean;
}

const ReviseQuoteDocumentPage: React.FC = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { publicId } = useParams<{ publicId: string }>();
  
  // Redux selectors
  const currentQuote = useSelector(selectQuotePublicCurrentQuote);
  const quoteItems = useSelector(selectQuotePublicQuoteItems);
  const isLoading = useSelector(selectIsQuotePublicDetailLoading);
  const error = useSelector(selectQuotePublicDetailError);
  
  const [supplierNotes, setSupplierNotes] = useState('');
  const [requesterNotes, setRequesterNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [validUntil, setValidUntil] = useState<number | ''>('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [vatIncluded, setVatIncluded] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSaveConfirmModal, setShowConfirmModal] = useState(false);
  const [showSaveInfoModal, setShowSaveInfoModal] = useState(false);
  const [showItemViewModal, setShowItemViewModal] = useState(false);
  const [itemToView, setItemToView] = useState<any>(null);
  const [isQuantityInputFocused, setIsQuantityInputFocused] = useState(false);
  const [editableValues, setEditableValues] = useState({
    quantity: 0 as number | string,
    unitPrice: 0,
    deliveryDays: 0 as number | string,
    vatRate: 0,
    targetRequestedQuantity: 0,
    targetRequestedPrice: 0,
    targetDeliveryDays: 0
  });

  const [products, setProducts] = useState<Product[]>([]);

  // Fetch quote public detail on component mount
  useEffect(() => {
    if (publicId) {
      dispatch(fetchQuotePublicDetail({ publicId }));
    }
  }, [dispatch, publicId]);

  // Allow page scrolling only on this public revise quote document screen.
  useEffect(() => {
    document.documentElement.classList.add('quote-document-scroll-enabled');

    return () => {
      document.documentElement.classList.remove('quote-document-scroll-enabled');
    };
  }, []);

  // Update products when quoteItems change
  useEffect(() => {
    if (quoteItems && quoteItems.length > 0) {
      const mappedProducts = quoteItems.map((item, index) => ({
        id: item.id || index + 1,
        itemId: item.itemId || item.id || index + 1, // itemId ekledik
        code: item.itemCode || '',
        name: item.itemName || '',
        requestedQuantity: item.requestedQuantity || 0,
        quantity: item.offeredQuantity || 0,
        targetRequestedQuantity: item.requestedQuantity || 0,
        unit: (item.unitName || '').toLowerCase() === 'ad' ? 'adet' : (item.unitName || ''),
        unitPrice: item.offeredPrice || 0,
        targetRequestedPrice: item.requestedPrice || 0,
        deliveryDays: item.offeredDeliveryDays || 0,
        targetDeliveryDays: item.requestedDeliveryDays || 0,
        vatRate: item.vatRate || 0,
        total: (item.offeredQuantity || 0) * (item.offeredPrice || 0),
        isApproved: false,
        isEdited: false
      }));
      setProducts(mappedProducts);
    }
  }, [quoteItems]);

  // Update notes when currentQuote changes
  useEffect(() => {
    if (currentQuote?.noteToSupplier) {
      setRequesterNotes(currentQuote.noteToSupplier);
    }
    if (currentQuote?.noteFromSupplier) {
      setSupplierNotes(currentQuote.noteFromSupplier);
    }
    
    // Para birimi ayarı:
    // Eğer backend'den para birimi geliyorsa, bunu sadece belirli durumlarda set et.
    // Kullanıcı varsayılan olarak boş gelmesini istediği için, eğer fiyat girilmemişse ve para birimi varsayılan (TRY) ise boş bırak.
    if (currentQuote?.currency) {
      const isDefaultCurrency = currentQuote.currency === 'TRY';
      const hasPricedItems = quoteItems && quoteItems.length > 0 && quoteItems.some(item => (item.offeredPrice || 0) > 0);
      
      // Eğer para birimi TRY değilse (yani değiştirilmişse) VEYA fiyat girilmiş ürün varsa set et.
      // Aksi takdirde (TRY ve fiyat yoksa) boş bırak ki kullanıcı seçmek zorunda kalsın.
      if (!isDefaultCurrency || hasPricedItems) {
        setSelectedCurrency(currentQuote.currency);
      } else {
        setSelectedCurrency('');
      }
    }
    
    if (currentQuote?.vatIncluded !== undefined) {
      setVatIncluded(currentQuote.vatIncluded);
    }
    if (currentQuote?.paymentTerm) {
      setPaymentTerms(currentQuote.paymentTerm);
    }
    if (currentQuote?.validUntil !== undefined && currentQuote?.validUntil !== null) {
      setValidUntil(currentQuote.validUntil);
    } else {
      setValidUntil('');
    }
  }, [currentQuote, quoteItems]);




  // Basit toplam hesaplama
  const getTotal = (unitPrice: number, quantity: number | string) => {
    const qty = typeof quantity === 'string' ? (quantity === '' ? 0 : parseFloat(quantity) || 0) : quantity;
    return unitPrice * qty;
  };

  const getSubtotal = () => {
    if (vatIncluded) {
      // KDV dahil ise, KDV'siz tutarı hesapla
      return products.reduce((sum, product) => {
        const productTotal = getTotal(product.unitPrice, product.quantity);
        const vatAmount = (productTotal * product.vatRate) / (100 + product.vatRate);
        return sum + (productTotal - vatAmount);
      }, 0);
    } else {
      // KDV hariç ise, direkt toplam
      return products.reduce((sum, product) => sum + getTotal(product.unitPrice, product.quantity), 0);
    }
  };

  const getVatBreakdown = () => {
    const vatGroups: { [key: number]: number } = {};
    
    products.forEach(product => {
      const productTotal = getTotal(product.unitPrice, product.quantity);
      let vatAmount = 0;
      
      if (vatIncluded) {
        // KDV dahil ise, KDV tutarını çıkar
        vatAmount = (productTotal * product.vatRate) / (100 + product.vatRate);
      } else {
        // KDV hariç ise, KDV tutarını ekle
        vatAmount = (productTotal * product.vatRate) / 100;
      }
      
      const existingAmount = vatGroups[product.vatRate];
      vatGroups[product.vatRate] = (existingAmount ?? 0) + vatAmount;
    });
    
    return vatGroups;
  };

  const getVatTotal = () => {
    const vatBreakdown = getVatBreakdown();
    return Object.values(vatBreakdown).reduce((sum, amount) => sum + amount, 0);
  };

  const getGrandTotal = () => {
    if (vatIncluded) {
      // KDV dahil ise, ürün fiyatları zaten KDV dahil
      return products.reduce((sum, product) => sum + getTotal(product.unitPrice, product.quantity), 0);
    } else {
      // KDV hariç ise, KDV'yi ekle
      return getSubtotal() + getVatTotal();
    }
  };

  // Son tarih hesaplama - emailSentAt + timeoutDays
  const getDeadlineDate = () => {
    if (currentQuote?.emailSentAt && currentQuote?.timeoutDays) {
      const emailDate = new Date(currentQuote.emailSentAt);
      const deadlineDate = new Date(emailDate);
      deadlineDate.setDate(deadlineDate.getDate() + currentQuote.timeoutDays);
      return deadlineDate;
    }
    return null;
  };


  const closeProductModal = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
    setIsEditMode(false);
    setIsQuantityInputFocused(false);
  };

  const handleEditProduct = (product: Product) => {
    // Open the same modal as product name click
    setSelectedProduct(product);
    setShowProductModal(true);
    setIsEditMode(true);
    setEditableValues({
      quantity: product.quantity,
      unitPrice: product.unitPrice,
      deliveryDays: product.deliveryDays,
      vatRate: product.vatRate || 0,
      targetRequestedQuantity: product.targetRequestedQuantity,
      targetRequestedPrice: product.targetRequestedPrice,
      targetDeliveryDays: product.targetDeliveryDays
    });
  };


  const handleSave = () => {
    setShowConfirmModal(true);
  };

  const handleSaveOnly = () => {
    setShowSaveInfoModal(true);
  };

  const executeSaveOnly = async () => {
    try {
      // Valid until validation
      if (validUntil === '' || validUntil === null || validUntil === undefined) {
        showError('Lütfen teklif geçerlilik süresini giriniz!');
        return;
      }

      // Ürün alanları validasyonu
      if (products.length > 0) {
        for (const product of products) {
          if (!product.quantity || product.quantity <= 0) {
            showError(`"${product.name}" için miktar girilmelidir!`);
            return;
          }
          if (!product.unitPrice || product.unitPrice <= 0) {
            showError(`"${product.name}" için birim fiyat girilmelidir!`);
            return;
          }
          if (product.deliveryDays === undefined || product.deliveryDays === null || product.deliveryDays <= 0) {
            showError(`"${product.name}" için teslimat süresi girilmelidir!`);
            return;
          }
        }
      }
      
      // İlk aşama: Her ürün için updateQuotePublicItem çağrısı
      if (publicId && products.length > 0) {
        for (const product of products) {
          const updateData = {
            offeredQuantity: product.quantity,
            offeredPrice: product.unitPrice,
            offeredDeliveryDays: product.deliveryDays,
            vatRate: product.vatRate || 0
          };
          
          const result = await dispatch(updateQuotePublicItem({ 
            publicId, 
            quoteItemId: product.id,
            data: updateData 
          }));
          
          // Success kontrolü - eğer başarısızsa işlemi durdur
          if (result.type.endsWith('/rejected')) {
            showError('Ürün güncellenirken hata oluştu!');
            return;
          }
        }
      }
      
      // İkinci aşama: updateQuotePublic çağrısı (sadece bir kez)
      if (publicId) {
        const quoteUpdateData = {
          noteFromSupplier: supplierNotes,
          paymentTerm: paymentTerms,
          currency: selectedCurrency,
          vatIncluded,
          validUntil: validUntil,
          quoteStatus: "Editing"
        };
        
        await dispatch(updateQuotePublic({ 
          publicId, 
          data: quoteUpdateData 
        }));
      }
      
      // Üçüncü aşama: Güncellenmiş teklif detaylarını yeniden getir
      if (publicId) {
        const refreshResult = await dispatch(fetchQuotePublicDetail({ publicId }));
        
        // 3. endpoint başarılı olduysa başarı mesajı
        if (refreshResult.type.endsWith('/fulfilled')) {
          showSuccess('Teklif belgesi başarıyla kaydedildi ve güncellendi!');
        }
      } else {
        showSuccess('Teklif belgesi kaydedildi!');
      }
    } catch (error) {
      showError('Teklif kaydedilirken bir hata oluştu!');
    }
  };

  const handleConfirmSave = async () => {
    try {
      // Valid until validation
      if (validUntil === '' || validUntil === null || validUntil === undefined) {
        showError('Lütfen teklif geçerlilik süresini giriniz!');
        return;
      }

      // Ürün alanları validasyonu
      if (products.length > 0) {
        for (const product of products) {
          if (!product.quantity || product.quantity <= 0) {
            showError(`"${product.name}" için miktar girilmelidir!`);
            return;
          }
          if (!product.unitPrice || product.unitPrice <= 0) {
            showError(`"${product.name}" için birim fiyat girilmelidir!`);
            return;
          }
          if (product.deliveryDays === undefined || product.deliveryDays === null || product.deliveryDays <= 0) {
            showError(`"${product.name}" için teslimat süresi girilmelidir!`);
            return;
          }
        }
      }
      
      setShowConfirmModal(false);
      
      // İlk aşama: Her ürün için updateQuotePublicItem çağrısı
      if (publicId && products.length > 0) {
        for (const product of products) {
          const updateData = {
            offeredQuantity: product.quantity,
            offeredPrice: product.unitPrice,
            offeredDeliveryDays: product.deliveryDays,
            vatRate: product.vatRate || 0
          };
          
          const result = await dispatch(updateQuotePublicItem({ 
            publicId, 
            quoteItemId: product.id,
            data: updateData 
          }));
          
          // Success kontrolü - eğer başarısızsa işlemi durdur
          if (result.type.endsWith('/rejected')) {
            showError('Ürün güncellenirken hata oluştu!');
            return;
          }
        }
      }
      
      // İkinci aşama: updateQuotePublic çağrısı (sadece bir kez)
      if (publicId) {
        const quoteUpdateData = {
          noteFromSupplier: supplierNotes,
          paymentTerm: paymentTerms,
          currency: selectedCurrency,
          vatIncluded,
          validUntil: validUntil,
          quoteStatus: "Responded"
        };
        
        const updateResult = await dispatch(updateQuotePublic({ 
          publicId, 
          data: quoteUpdateData 
        }));
        
        // Başarı kontrolü
        if (updateResult.type.endsWith('/rejected')) {
          showError('Teklif güncellenirken hata oluştu!');
          return;
        }
        
        // Üçüncü aşama: Tedarikçiye onay emaili gönder (kritik değil)
        if (publicId) {
          try {
            await dispatch(sendQuotePublicConfirmation(publicId)).unwrap();
          } catch {
            showWarning('Onay emaili gönderilemedi, ancak teklifiniz başarıyla kaydedildi.');
          }
        }

        // Başarılıysa success mesajını göster
        setShowSuccessMessage(true);
      } else {
        setShowSuccessMessage(true);
      }
    } catch (error) {
      showError('Teklif kaydedilirken bir hata oluştu!');
    }
  };

  const openItemView = (itemId: number) => {
    console.log('openItemView called with itemId:', itemId);
    
    // Response'dan gelen ürün verilerini bul
    const itemFromQuote = quoteItems?.find(item => item.itemId === itemId);
    
    if (itemFromQuote?.item) {
      // Response'dan gelen verileri kullan
      setItemToView({ 
        itemId,
        itemFromResponse: itemFromQuote
      });
    } else {
      // Fallback - sadece itemId ile
      setItemToView({ itemId });
    }
    
    setShowItemViewModal(true);
  };

  const closeItemViewModal = () => {
    setShowItemViewModal(false);
    setItemToView(null);
  };

  // Show success message after successful save and approve
  if (showSuccessMessage) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 max-w-2xl mx-auto">
            <div className="mb-8">
              <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-green-100 dark:bg-green-900/20 mb-6">
                <i className="fas fa-check-circle text-green-600 dark:text-green-400 text-4xl"></i>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                İşlem Başarılı!
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                Revize Teklifiniz Başarıyla Gönderilmiştir
              </p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Teklif belgesi yükleniyor...</p>
          </div>
        </div>
      </main>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 max-w-2xl mx-auto">
            <div className="mb-8">
              <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-red-100 dark:bg-red-900/20 mb-6">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400 text-4xl"></i>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Link Geçersiz!
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Bu teklif linki geçersiz veya süresi dolmuş olabilir. 
                <br />
                Lütfen doğru link ile tekrar deneyiniz.
              </p>
              <button 
                onClick={() => {
                  if (publicId) {
                    dispatch(fetchQuotePublicDetail({ publicId }));
                  }
                }}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
              >
                <i className="fas fa-redo mr-2"></i>Tekrar Dene
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">


        {/* Document Container */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transition-colors duration-300">

          {/* Document Header */}
          <div className="bg-gradient-to-r from-primary to-secondary text-white p-6">
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-4">
                <img
                  className="w-48 h-48 object-contain -mt-12 -mb-12"
                  src={MasLogo}
                  alt="MAS Logo"
                />
                <div>
                  <h3 className="text-2xl font-bold mb-2">REVİZE TEKLİF BELGESİ</h3>
                  <p className="text-blue-100">Belge No: {currentQuote?.id ? `${currentQuote.id}` : '-'}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-sm text-blue-100 mb-1">Son Tarih</div>
                  <div className="text-lg font-bold text-white">
                    {getDeadlineDate() ? formatDate(getDeadlineDate()!) : '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Document Body */}
          <div className="p-6">

            {/* Company Information */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 transition-colors">Talep Eden Şirket</h4>
                <div className="space-y-1 text-gray-700 dark:text-gray-300 transition-colors">
                  <p>MAS Masaüstü ve Ofis Gereçleri San. ve Tic. A.Ş</p>
                  <p>İkitelli OSB, Eski Turgut Özal Cd. No:28, 34306 Başakşehir/İstanbul</p>
                  <p>İstanbul</p>
                  <p>(0212) 549 07 07</p>
                  <p>mas@masburo.com</p>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 transition-colors">Tedarikçi Bilgileri</h4>
                <div className="space-y-1 text-gray-700 dark:text-gray-300 transition-colors">
                  <p>{currentQuote?.supplier?.companyName || currentQuote?.supplierName || '-'}</p>
                  <p>{currentQuote?.supplier?.contactPerson || '-'}</p>
                  <p>{currentQuote?.supplier?.address || '-'}</p>
                  <p>{currentQuote?.supplier?.city || '-'}</p>
                  <p>{currentQuote?.supplier?.phone || '-'}</p>
                  <p>{currentQuote?.supplier?.email || currentQuote?.supplierEmail || '-'}</p>
                </div>
              </div>
            </div>

            {/* Requester Notes Section */}
            {requesterNotes && requesterNotes.trim() && (
              <div className="mb-8">
                <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">
                </div>
                <div className="bg-yellow-200 dark:bg-yellow-600 rounded-lg p-4 border-2 border-yellow-400 dark:border-yellow-500 shadow-lg transition-colors">
                  <div className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed transition-colors">
                    {requesterNotes}
                  </div>
                </div>
              </div>
            )}

                        {/* Products Section */}
            <div className="mb-8">
              <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg transition-colors">Talep Edilen Ürünler</h4>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Para Birimi:</label>
                    <select 
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                      className={`px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors ${!selectedCurrency ? 'border-red-500 ring-2 ring-red-200' : ''}`}
                    >
                      <option value="" disabled>Seçiniz</option>
                      <option value="TRY">TRY</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">KDV:</label>
                    <select 
                      value={vatIncluded ? 'included' : 'excluded'}
                      onChange={(e) => setVatIncluded(e.target.value === 'included')}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                    >
                      <option value="excluded">KDV Hariç</option>
                      <option value="included">KDV Dahil</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                                  <table className="w-full border border-gray-300 dark:border-gray-600 table-fixed">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      {/* Grouped Header Row */}
                      <tr>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" rowSpan={2}>Ürün Kodu</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" rowSpan={2}>Ürün Adı</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" rowSpan={2}>Birim</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors bg-green-100 dark:bg-green-900/30" colSpan={3}>Revizyon Talebimiz</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors bg-blue-100 dark:bg-blue-900/30" colSpan={3}>Teklifiniz</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" rowSpan={2}>İşlemler</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors" rowSpan={2} style={{ minWidth: '120px' }}>Tutar</th>

                      </tr>
                      {/* Individual Column Headers */}
                      <tr>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Miktar</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Birim Fiyat</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Teslimat Süresi (Gün)</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Miktar</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Birim Fiyat</th>
                        <th className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Teslimat Süresi (Gün)</th>
                      </tr>
                    </thead>
                  <tbody>
                    {products.map((product) => (
                                             <tr key={product.id} className={`transition-colors ${product.isApproved ? 'bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50' : product.isEdited ? 'bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-gray-900 dark:text-white transition-colors">
                          {product.code}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-gray-900 dark:text-white transition-colors">
                          <span 
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
                            onClick={() => openItemView(product.itemId)}
                            title={product.code?.startsWith('PRJ') ? "Proje detayları" : "Ürün detayları"}
                          >
                            {product.name}
                          </span>
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center text-gray-900 dark:text-white transition-colors">{product.unit}</td>

                        {/* Revizyon Talebimiz - Sol taraf */}
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center text-gray-900 dark:text-white transition-colors">
                          {formatQuantity(product.targetRequestedQuantity)}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center text-gray-900 dark:text-white transition-colors">
                          {product.targetRequestedPrice.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center text-gray-900 dark:text-white transition-colors">
                          {product.targetDeliveryDays}
                        </td>
                        {/* Teklifiniz - Sağ taraf */}
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center text-gray-900 dark:text-white transition-colors">
                          {formatQuantity(product.quantity)}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center text-gray-900 dark:text-white transition-colors">
                          {product.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center text-gray-900 dark:text-white transition-colors">
                          {product.deliveryDays}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-center transition-colors">
                          <button
                            onClick={() => handleEditProduct(product)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-sm font-medium transition-colors"
                          >
                            <i className="fas fa-edit mr-1"></i>Düzenle
                          </button>
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-3 text-sm text-right font-medium text-gray-900 dark:text-white transition-colors whitespace-nowrap">
                          {getTotal(product.unitPrice, product.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </td>

                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* Ara Toplam */}
                    <tr className="bg-gray-50 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600">
                      <td colSpan={9} className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-right font-semibold text-sm text-gray-700 dark:text-gray-300 transition-colors">
                        <i className="fas fa-calculator mr-1"></i>ARA TOPLAM:
                      </td>
                      <td colSpan={2} className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-right font-bold text-sm text-gray-700 dark:text-gray-300 transition-colors whitespace-nowrap">
                        {getSubtotal().toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                      </td>
                    </tr>
                    
                    {/* KDV Detayları - Her KDV oranı için ayrı satır */}
                    {Object.entries(getVatBreakdown()).map(([vatRate, amount]) => (
                      <tr key={vatRate} className="bg-gray-50 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600">
                        <td colSpan={9} className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-right font-semibold text-sm text-gray-700 dark:text-gray-300 transition-colors">
                          <i className="fas mr-1"></i>KDV (%{vatRate}) TUTARI:
                        </td>
                        <td colSpan={2} className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-right font-bold text-sm text-gray-700 dark:text-gray-300 transition-colors whitespace-nowrap">
                          {amount.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                        </td>
                      </tr>
                    ))}
                    
                    {/* Genel Toplam */}
                    <tr className="bg-gray-100 dark:bg-gray-600 border-t-2 border-gray-400 dark:border-gray-500">
                      <td colSpan={9} className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-right font-bold text-base text-gray-800 dark:text-gray-200 transition-colors">
                        <i className="fas fa-coins mr-1"></i>GENEL TOPLAM:
                      </td>
                      <td colSpan={2} className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-right font-bold text-base text-gray-800 dark:text-gray-200 transition-colors whitespace-nowrap">
                        {getGrandTotal().toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Payment Terms and Validity Days Section */}
            <div className={`mb-8 grid gap-6 ${currentQuote?.supplier?.showPaymentTerm === true ? 'grid-cols-[2fr_1fr]' : 'grid-cols-1'}`}>
              {/* Payment Terms Section - Only show if showPaymentTerm === true */}
              {currentQuote?.supplier?.showPaymentTerm === true && (
                <div className="flex flex-col">
                  <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">Ödeme Koşulları</h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-300 dark:border-gray-600 transition-colors flex-1 flex flex-col">
                    <textarea 
                      className="w-full h-24 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors flex-1"
                      placeholder="Ödeme koşullarınızı buraya girebilirsiniz (örn: 30 gün vadeli, peşin ödeme, %50 peşin %50 teslimde vb.)..."
                      value={paymentTerms}
                      onChange={(e) => setPaymentTerms(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Validity Days Section */}
              <div className="flex flex-col">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">Teklif Geçerlilik Süresi <span className="text-red-500">*</span></h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-300 dark:border-gray-600 transition-colors flex-1 flex flex-col justify-center">
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                      Teklif geçerlilik süresi: <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={validUntil === '' ? '' : validUntil}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setValidUntil('');
                          } else {
                            const numValue = parseInt(value);
                            if (!isNaN(numValue) && numValue > 0) {
                              setValidUntil(numValue);
                            }
                          }
                        }}
                        className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-colors [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors">gün</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="mb-8">
              <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 mb-4 transition-colors">Tedarikçi Notları</h4>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 transition-colors">
                <textarea
                  className="w-full h-32 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                  placeholder="Tedarikçi notlarınızı buraya girebilirsiniz..."
                  value={supplierNotes}
                  onChange={(e) => setSupplierNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Terms Section */}
            <div className="space-y-8 mb-8">
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white text-lg border-b border-gray-200 dark:border-gray-600 pb-2 transition-colors">Teslimat Bilgileri</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-32 mt-2 transition-colors">Teslim Yeri:</label>
                      <div className="flex-1">
                        <div className="text-gray-700 dark:text-gray-300 text-sm mb-2 transition-colors">
                          İkitelli OSB, Eski Turgut Özal Cd. No:28, 34306 Başakşehir/İstanbul
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 text-xs italic transition-colors">
                          Belirtilen mallar belirtilen sürede tedarikçi tarafından bu adrese teslim edilecektir.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>



        {/* Bottom Action Buttons */}
        <div className="mt-8 space-y-6 no-print">
          {/* Bilgilendirme Mesajları */}
          <div className="flex justify-between gap-4">
            {/* Sol Bilgilendirme */}
            <div className="flex-1 bg-blue-50 border border-blue-200 rounded-md p-3 hidden">
              <div className="flex items-start space-x-2">
                <i className="fas fa-info-circle text-blue-500 mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Kaydet ve Sonra Devam Et:</strong> Teklifinizi taslak olarak kaydeder. Daha sonra düzenlemeye devam edebilir ve tekrar kaydedebilirsiniz.
                </p>
              </div>
            </div>
            
            {/* Sağ Bilgilendirme */}
            <div className="flex-1 bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <i className="fas fa-info-circle text-blue-500 mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Kaydet & Onayla:</strong> Teklifinizi kaydeder ve onaylar. Onayladıktan sonra bu teklifi değiştiremezsiniz.
                </p>
              </div>
            </div>
          </div>

          {/* Butonlar */}
          <div className="flex justify-center gap-4">
            <button 
              onClick={handleSaveOnly}
              className="bg-gray-600 text-white px-8 py-3 rounded-lg hover:bg-gray-700 transition-colors text-lg font-medium hidden"
            >
              <i className="fas fa-save mr-2"></i>Kaydet ve Sonra Devam Et
            </button>
            <button 
              onClick={handleSave}
              className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
            >
              <i className="fas fa-check-circle mr-2"></i>Kaydet & Onayla
            </button>
          </div>
        </div>

        {/* Product Modal */}
        {showProductModal && selectedProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto transition-colors duration-300">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white transition-colors">
                    Ürün Detayları - {selectedProduct.name}
                  </h3>
                  <button
                    onClick={closeProductModal}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Info Section */}
                <div className="mb-4">
                  <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-5 h-5 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700 dark:text-blue-300 leading-relaxed">
                        Teklifiniz bölümünde görüntülenen değerler son verdiğiniz teklifin değerleridir. Bu değerleri revize edebilirsiniz.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Product Card */}
                  <div className="bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-500">
                    {/* Card Content */}
                    <div className="p-4">
                                             <div className="space-y-4">
                         {/* Section Headers */}
                         <div className="grid grid-cols-2 gap-6">
                           <h4 className="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 pb-2">
                             Revizyon Talebi
                           </h4>
                           <h4 className="text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 pb-2">
                             Teklifiniz
                           </h4>
                         </div>

                         {/* Content Sections */}
                         <div className="grid grid-cols-2 gap-6">
                           {/* Revizyon Information */}
                           <div className="bg-green-50 dark:bg-green-900/20 rounded p-3 border border-green-200 dark:border-green-700">
                             <div className="space-y-4">
                               <div className="flex justify-between items-center min-h-[36px]">
                                 <span className="text-sm text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">Miktar:</span>
                                 <div className="flex items-center flex-1 justify-end min-h-[34px]">
                                   <span className="text-sm text-gray-900 dark:text-white font-medium text-right">{selectedProduct.targetRequestedQuantity} {selectedProduct.unit}</span>
                                 </div>
                               </div>
                               <div className="flex justify-between items-center min-h-[36px]">
                                 <span className="text-sm text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">Birim Fiyat:</span>
                                 <div className="flex items-center flex-1 justify-end min-h-[34px]">
                                   <span className="text-sm text-gray-900 dark:text-white font-medium text-right">
                                     {selectedProduct.targetRequestedPrice.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                                   </span>
                                 </div>
                               </div>
                               <div className="flex justify-between items-center min-h-[36px]">
                                 <span className="text-sm text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">Teslimat (Gün):</span>
                                 <div className="flex items-center flex-1 justify-end min-h-[34px]">
                                   <span className="text-sm text-gray-900 dark:text-white font-medium text-right">{selectedProduct.targetDeliveryDays} gün</span>
                                 </div>
                               </div>
                               <div className="flex justify-between items-center border-t border-green-200 dark:border-green-600 pt-3 mt-3 min-h-[36px]">
                                 <span className="text-sm text-gray-600 dark:text-gray-400 font-semibold w-32 flex-shrink-0">Toplam:</span>
                                 <div className="flex items-center flex-1 justify-end min-h-[34px]">
                                   <span className="text-sm text-gray-900 dark:text-white font-bold text-right">
                                     {(selectedProduct.targetRequestedQuantity * selectedProduct.targetRequestedPrice).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {selectedCurrency}
                                   </span>
                                 </div>
                               </div>
                             </div>
                           </div>

                           {/* Teklifiniz Information */}
                           <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-3 border border-blue-200 dark:border-blue-700">
                             <div className="space-y-4">
                               <div className="flex justify-between items-center min-h-[36px]">
                                 <span className="text-sm text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">Miktar:</span>
                                  <div className="flex items-center flex-1 justify-end min-h-[34px]">
                                   <input
                                     type="text"
                                     disabled={!selectedCurrency}
                                     value={
                                       editableValues.quantity === '' 
                                         ? '' 
                                         : isQuantityInputFocused
                                           ? (typeof editableValues.quantity === 'number' ? editableValues.quantity.toString() : editableValues.quantity.toString())
                                           : (typeof editableValues.quantity === 'number' ? formatQuantity(editableValues.quantity) : formatQuantity(parseInt(editableValues.quantity.toString()) || 0))
                                     }
                                     onChange={(e) => {
                                       const value = e.target.value;
                                       // Sadece sayıları kabul et (format karakterlerini temizle)
                                       const cleanedValue = value.replace(/[^\d]/g, '');
                                       if (cleanedValue === '') {
                                         setEditableValues({...editableValues, quantity: ''});
                                       } else {
                                         const numValue = parseInt(cleanedValue, 10);
                                         if (!isNaN(numValue) && numValue >= 0) {
                                           setEditableValues({...editableValues, quantity: numValue});
                                         }
                                       }
                                     }}
                                     onBlur={(e) => {
                                       setIsQuantityInputFocused(false);
                                       // Blur olduğunda formatı düzelt
                                       const value = e.target.value;
                                       const cleanedValue = value.replace(/[^\d]/g, '');
                                       if (cleanedValue === '') {
                                         setEditableValues({...editableValues, quantity: ''});
                                       } else {
                                         const numValue = parseInt(cleanedValue, 10);
                                         if (!isNaN(numValue) && numValue >= 0) {
                                           setEditableValues({...editableValues, quantity: numValue});
                                         }
                                       }
                                     }}
                                     onFocus={(e) => {
                                       setIsQuantityInputFocused(true);
                                       // State güncellemesi tamamlandıktan sonra select yap
                                       setTimeout(() => {
                                         e.target.select();
                                       }, 0);
                                     }}
                                     className={`w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-l bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-right ${!selectedCurrency ? 'opacity-50 cursor-not-allowed' : ''}`}
                                   />
                                   <span className="w-12 px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r text-center">{selectedProduct.unit}</span>
                                 </div>
                               </div>
                               <div className="flex justify-between items-center min-h-[36px]">
                                 <span className="text-sm text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">Birim Fiyat:</span>
                                 <div className="flex items-center flex-1 justify-end min-h-[34px]">
                                   <PriceInput
                                     value={editableValues.unitPrice}
                                     disabled={!selectedCurrency}
                                     onChange={(value) => setEditableValues({...editableValues, unitPrice: value})}
                                     className={`w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-l bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${!selectedCurrency ? 'opacity-50 cursor-not-allowed' : ''}`}
                                     onFocus={(e) => e.target.select()}
                                     allowNegative={false}
                                     decimalScale={4}
                                     fixedDecimalScale={true}
                                   />
                                   <span className="w-12 px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r text-center">{selectedCurrency}</span>
                                 </div>
                               </div>
                               <div className="flex justify-between items-center min-h-[36px]">
                                 <span className="text-sm text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">Teslimat (Gün):</span>
                                 <div className="flex items-center flex-1 justify-end min-h-[34px]">
                                   <input
                                     type="number"
                                     disabled={!selectedCurrency}
                                     value={editableValues.deliveryDays}
                                     onChange={(e) => {
                                       const value = e.target.value;
                                       if (value === '') {
                                         setEditableValues({...editableValues, deliveryDays: ''});
                                       } else {
                                         const numValue = parseInt(value);
                                         setEditableValues({...editableValues, deliveryDays: isNaN(numValue) ? 0 : numValue});
                                       }
                                     }}
                                     onFocus={(e) => e.target.select()}
                                     className={`w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-l bg-white dark:bg-gray-800 text-gray-900 dark:text-white [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] ${!selectedCurrency ? 'opacity-50 cursor-not-allowed' : ''}`}
                                   />
                                   <span className="w-12 px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r text-center">gün</span>
                                 </div>
                               </div>
                               <div className="flex justify-between items-center border-t border-blue-200 dark:border-blue-600 pt-3 mt-3 min-h-[36px]">
                                 <span className="text-sm text-gray-600 dark:text-gray-400 font-semibold w-32 flex-shrink-0">Toplam:</span>
                                 <div className="flex items-center flex-1 justify-end min-h-[34px]">
                                   <span className="text-sm text-gray-900 dark:text-white font-bold text-right">
                                     {(() => {
                                       const qty = typeof editableValues.quantity === 'string' ? (editableValues.quantity === '' ? 0 : parseFloat(editableValues.quantity) || 0) : editableValues.quantity;
                                       return (qty * editableValues.unitPrice).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
                                     })()} {selectedCurrency}
                                   </span>
                                 </div>
                               </div>
                             </div>
                           </div>
                         </div>
                      </div>


                      {/* Save and Cancel Buttons - Only visible in edit mode */}
                      {isEditMode && (
                        <div className="mt-6 flex justify-between items-center">
                          <button
                            onClick={() => {
                              // Revizyon talebindeki değerleri teklifiniz değerlerine kopyala
                              setEditableValues({
                                ...editableValues,
                                quantity: editableValues.targetRequestedQuantity,
                                unitPrice: editableValues.targetRequestedPrice,
                                deliveryDays: editableValues.targetDeliveryDays
                              });
                            }}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                          >
                            <i className="fas fa-check mr-2"></i>Revizyon Talebini Kabul Et
                          </button>
                          <div className="flex space-x-3">
                            <button
                              onClick={() => {
                                closeProductModal();
                              }}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 hover:border-gray-400 rounded-lg transition-colors font-medium"
                            >
                              İptal
                            </button>
                            <button
                              onClick={() => {
                                const qty = typeof editableValues.quantity === 'string' ? (editableValues.quantity === '' ? 0 : parseFloat(editableValues.quantity) || 0) : editableValues.quantity;
                                const deliveryDays = typeof editableValues.deliveryDays === 'string' ? (editableValues.deliveryDays === '' ? 0 : parseInt(editableValues.deliveryDays) || 0) : editableValues.deliveryDays;
                                const newTotal = qty * editableValues.unitPrice;
                                const updatedProducts = products.map(p => 
                                  p.id === selectedProduct.id ? { 
                                    ...p, 
                                    isEdited: true,
                                    isApproved: false, // Düzenleme yapıldığında onay durumunu sıfırla
                                    quantity: qty,
                                    unitPrice: editableValues.unitPrice,
                                    deliveryDays: deliveryDays,
                                    vatRate: editableValues.vatRate,
                                    targetRequestedQuantity: editableValues.targetRequestedQuantity,
                                    targetRequestedPrice: editableValues.targetRequestedPrice,
                                    targetDeliveryDays: editableValues.targetDeliveryDays,
                                    total: newTotal
                                  } : p
                                );
                                setProducts(updatedProducts);
                                setIsEditMode(false);
                                closeProductModal();
                              }}
                              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                            >
                              Uygula
                            </button>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>

              {/* Revision History Section */}
              <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                  Revizyon Geçmişi
                </h4>
                <div className="space-y-3">
                  {(() => {
                    // Find the quote item that matches the selected product
                    const quoteItem = quoteItems?.find(item => item.itemId === selectedProduct.itemId);
                    // Type assertion for history since it's not in the generated types yet
                    const historyItems = (quoteItem as any)?.history || [];
                    
                    if (historyItems.length === 0) {
                      return (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                          Revizyon geçmişi bulunmamaktadır.
                        </div>
                      );
                    }
                    
                    // Reverse the array to show newest items first
                    const reversedHistory = [...historyItems].reverse();
                    
                    return (
                      <div className="space-y-3">
                        {reversedHistory.map((historyItem, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 transition-colors hover:shadow-md"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400"></div>
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {historyItem.step || 'Bilinmeyen Adım'}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {historyItem.stageDate ? formatDate(new Date(historyItem.stageDate)) : '-'}
                              </span>
                            </div>
                            <div className="grid grid-cols-4 gap-4 mt-3">
                              <div>
                                <span className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Miktar</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {formatQuantity(historyItem.quantity || 0)} {selectedProduct.unit}
                                </span>
                              </div>
                              <div>
                                <span className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Birim Fiyat</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {(historyItem.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {historyItem.currency || selectedCurrency}
                                </span>
                              </div>
                              <div>
                                <span className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Teslimat (Gün)</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {historyItem.deliveryDays || 0} gün
                                </span>
                              </div>
                              <div>
                                <span className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Toplam Tutar</span>
                                <span className="text-sm font-bold text-gray-900 dark:text-white">
                                  {((historyItem.quantity || 0) * (historyItem.price || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {historyItem.currency || selectedCurrency}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Confirmation Modal */}
        <ConfirmationModal
          isOpen={showSaveConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmSave}
          title="Teklif Kaydet ve Onayla"
          message="Bu teklif belgesini kaydetmek ve onaylamak istediğinizden emin misiniz? Onayladıktan sonra bu teklifi bir daha değiştiremezsiniz."
          confirmText="Evet, Kaydet ve Onayla"
          type="info"
          icon="fas fa-check-circle"
        />

        {/* Save Info Modal */}
        <ConfirmationModal
          isOpen={showSaveInfoModal}
          onClose={() => setShowSaveInfoModal(false)}
          onConfirm={async () => {
            setShowSaveInfoModal(false);
            await executeSaveOnly();
          }}
          title="Teklif Kaydet"
          message="Teklif bilgileriniz taslak olarak kaydedildi. Fiyatlandırmayı tamamladıktan sonra 'Kaydet ve Onayla' butonuna basmayı unutmayınız."
          confirmText="Kaydet"
          type="info"
          icon="fas fa-save"
        />

        {/* ItemViewPublic Modal */}
        {showItemViewModal && itemToView && (
          <ItemViewPublic
            isOpen={showItemViewModal}
            onClose={closeItemViewModal}
            itemId={itemToView.itemId}
            itemFromResponse={itemToView.itemFromResponse}
            mode="view"
          />
        )}

      </main>
  );
};

export default ReviseQuoteDocumentPage;
